import React from 'react';
import styled from 'styled-components';

import { useGetDefaultShortlistLimit } from '../../../common/hooks/useGetDefaultShortlistLimit';
import useIsMonthlyQuotaEnabled from '../../../common/hooks/useIsMonthlyQuotaEnabled';
import useIsPremiumFiltersEnabled from '../../../common/hooks/useIsPremiumFiltersEnabled';
import useIsProfileLimitAccessEnabled from '../../../common/hooks/useIsProfileLimitAccessEnabled';
import { getVIPMembershipBenefits } from './constants';
import GlintsVIPBenefit from './GlintsVIPBenefit';
import GlintsVIPFreeCreditBenefit from './GlintsVIPFreeCreditBenefit';

type BaseProps = {
  hideDescription?: boolean;
  includeComingSoon?: boolean;
  showLearnMore?: boolean;
  showFullProfileAccess?: boolean;
};

type FreeCreditProps =
  | {
      includeFreeCreditBenefit?: false;
      freeCreditAmount?: never;
    }
  | {
      includeFreeCreditBenefit: true;
      freeCreditAmount: number;
    };

type Props = BaseProps & FreeCreditProps;

export default function GlintsVIPBenefits({
  hideDescription,
  includeComingSoon = false,
  includeFreeCreditBenefit = false,
  showLearnMore,
  showFullProfileAccess,
  freeCreditAmount: amount,
}: Props) {
  const isPremiumFilterEnabled = useIsPremiumFiltersEnabled();
  const isFullProfileAccessEnabled = useIsProfileLimitAccessEnabled({
    skipVIPMembershipCheck: true,
  });
  const { defaultDailyVIPLimitBalance, defaultVIPShortlistQuotaBalance } =
    useGetDefaultShortlistLimit();
  const isMonthlyQuotaEnabled = useIsMonthlyQuotaEnabled();
  const benefits = getVIPMembershipBenefits({
    isPremiumFilterEnabled,
    showFullProfileAccess: showFullProfileAccess || isFullProfileAccessEnabled,
    defaultDailyVIPLimitBalance,
    isMonthlyQuotaEnabled,
    defaultVIPShortlistQuota: defaultVIPShortlistQuotaBalance,
  }).filter(props => {
    const isComingSoon = 'isComingSoon' in props;
    return includeComingSoon || !isComingSoon;
  });

  return (
    <BenefitsGrid>
      {benefits.map(benefit => (
        <GlintsVIPBenefit
          key={benefit.heading.id}
          {...benefit}
          showLearnMore={showLearnMore}
          hideDescription={hideDescription}
        />
      ))}
      {includeFreeCreditBenefit && amount && (
        <GlintsVIPFreeCreditBenefit
          hideDescription={hideDescription}
          amount={amount}
        />
      )}
    </BenefitsGrid>
  );
}

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-template-rows: auto auto;
  grid-gap: 20px;
`;
